import React, { useEffect, useState } from "react";
import { FaRegEdit, FaUserGraduate } from "react-icons/fa";
import { RiDeleteBin6Line, RiExchangeFundsLine } from "react-icons/ri";
import axios from "axios";

import ClientStatusModal from "../../../components/Modals/ClientStatusModal";
import DeleteClientModal from "../../../components/Modals/DeleteClientModal";
import EditClientModal from "../../../components/Modals/EditClientModal";
import ClientDetailsModal from "../../Modals/ClientDetailsModal";

const ApprovedClients = ({ query, page, itemsPerPage, onDataChange }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/our-client/get-clients`
      );
      const approvedClients = res.data.filter(
        (item) => item?.status === "approved"
      );
      setClients(approvedClients);

      const totalFiltered = approvedClients.filter((item) =>
        (item?.clientName || item?.name || "")
          .toLowerCase()
          .includes((query || "").toLowerCase())
      );
      const totalPages = Math.ceil(totalFiltered.length / itemsPerPage);
      onDataChange(totalPages, totalFiltered.length);
    } catch (err) {
      console.error("Error fetching approved clients:", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = clients.filter((item) =>
    (item?.clientName || item?.name || "")
      .toLowerCase()
      .includes((query || "").toLowerCase())
  );

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClients = filteredData.slice(startIndex, endIndex);

  const handleEditClick = (client) => {
    setSelectedClient(client);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (client) => {
    setSelectedClient(client);
    setDeleteModalOpen(true);
  };

  const handleStatusClick = (client) => {
    setSelectedClient(client);
    setStatusModalOpen(true);
  };

  const handleDetailsClick = (client) => {
    setSelectedClient(client);
    setDetailsModalOpen(true);
  };

  const closeModals = () => {
    setStatusModalOpen(false);
    setEditModalOpen(false);
    setDeleteModalOpen(false);
    setDetailsModalOpen(false);
    setSelectedClient(null);
    fetchData();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedClient || !selectedClient._id) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/our-client/delete-client/${selectedClient._id}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          data: {
            email: selectedClient.email || "",
          },
        }
      );
      closeModals();
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete client.");
    }
  };

  return (
    <section className="px-2 mb-4">
      {paginatedClients.length === 0 ? (
        <div className="flex justify-center">
          <p className="p-4 text-gray-500">No approved clients found.</p>
        </div>
      ) : (
        <div className="overflow-x-auto sm:overflow-visible">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 min-w-[300px] sm:min-w-0 py-2">
            {paginatedClients.map((client, index) => (
              <div
                key={index}
                className="relative bg-white p-5 rounded-lg shadow-lg flex flex-col items-center text-center"
              >
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    className="text-blue-600"
                    onClick={() => handleEditClick(client)}
                    aria-label="Edit client"
                  >
                    <FaRegEdit className="h-5 w-5" />
                  </button>
                  <button
                    className="text-red-600"
                    onClick={() => handleDeleteClick(client)}
                    aria-label="Delete client"
                  >
                    <RiDeleteBin6Line className="h-5 w-5" />
                  </button>
                </div>

                <p className="absolute top-2 left-2 bg-green-600 rounded-2xl text-white text-xs font-normal tracking-wide py-1 px-2">
                  Approved
                </p>

                <FaUserGraduate className="w-20 h-20 rounded-full object-cover mb-4 border-2 border-white shadow-md text-[#1B81B2]" />

                <h3 className="font-semibold text-gray-900">
                  {client.clientName || client.name}
                </h3>
                <p className="text-sm text-blue-600 mt-1 bg-blue-50 px-3 py-1 rounded-full">
                  {client.email}
                </p>

                <div className="w-full flex justify-between gap-4 px-4">
                  <div className="w-full">
                    <button
                      type="button"
                      onClick={() => handleDetailsClick(client)}
                      className="mt-6 w-full bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white tracking-wide font-medium py-1.5 rounded-lg text-sm shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      Details
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => handleStatusClick(client)}
                      className="mt-6 w-full bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white tracking-wide font-medium px-1.5 py-1.5 rounded-lg text-sm md:text-base shadow-md hover:shadow-lg transition-all duration-300"
                      aria-label="Change client status"
                    >
                      <RiExchangeFundsLine size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {statusModalOpen && selectedClient && (
        <ClientStatusModal client={selectedClient} onClose={closeModals} />
      )}

      {deleteModalOpen && selectedClient && (
        <DeleteClientModal
          itemName={selectedClient.clientName || selectedClient.name}
          onClose={closeModals}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {editModalOpen && selectedClient && (
        <EditClientModal
          client={selectedClient}
          onClose={closeModals}
          onSubmit={() => {
            closeModals();
          }}
        />
      )}

      {detailsModalOpen && selectedClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-y-auto">
          <div className="max-w-full w-full sm:max-w-lg">
            <ClientDetailsModal client={selectedClient} onClose={closeModals} />
          </div>
        </div>
      )}
    </section>
  );
};

export default ApprovedClients;
